import React, { FunctionComponent, useMemo } from 'react';

import {
  HeaderContainer as UIHeaderContainer,
  Icon,
  type HeaderContainerProps,
} from '@bumble/ui-kit';
import { header, common } from 'components/lexemes';
import { SUPPORT_URL } from 'lib/constants';
import { useGlobalProps } from 'lib/hooks/use-global-props';
import { useHeaderScrolled } from 'lib/hooks/use-header-scrolled';
import interfaceLanguages from 'lib/languages.json';
import { getLocaleFromParams } from 'lib/localization';
import { useRouter } from 'next/router';

import { onMenuItemClickHandler } from 'containers/events';

import SkipLink from 'components/skip-link/skip-link';

// TODO: better detection of active page
function setActivePages(pages: HeaderContainerProps['menuItems'] = [], currentUrl: string) {
  pages.forEach((page: HeaderContainerProps['menuItems'][number]) => {
    // Check if the current URL starts with the page URL
    if (page.url && currentUrl.startsWith(page.url)) {
      // Ensure that the match is exact or followed by a '/'
      const nextChar = currentUrl[page.url.length];
      if (!nextChar || nextChar === '/') {
        page.isActive = true;
      } else {
        page.isActive = false;
      }
    } else {
      page.isActive = false;
    }

    // Recursively check submenu if it exists
    if (page.subMenu?.items && page.subMenu?.items?.length > 0) {
      setActivePages(page.subMenu.items, currentUrl);
      // If any dropdown item is active, mark the parent as active too
      // page.isActive = page.subMenu.items.some((subPage) => subPage.isActive);
    }
  });
}

type Props = {
  isDarkMode?: boolean;
  activeMenu?: 'date' | 'friends' | 'stories';
};

export const HeaderContainer: FunctionComponent<Props> = ({ isDarkMode = false, activeMenu }) => {
  const globalProps = useGlobalProps();
  useHeaderScrolled();
  const router = useRouter();
  const lang = globalProps?.lang;

  const languagesList = useMemo(() => {
    return interfaceLanguages.map(({ name, code }) => ({
      title: name,
      value: code,
      href: `/${code}`,
    }));
  }, []);

  const headerMenuItems: HeaderContainerProps['menuItems'] = [
    {
      title: header.lxHeaderItemLabelDate,
      isActive: activeMenu === 'date',

      subMenu: {
        title: header.lxHeaderModalDateTitle,
        titleUrl: `/${lang}/date/`,
        description: header.lxHeaderModalDateDescription,
        backButton: {
          text: 'Back',
          onClick: () => onMenuItemClickHandler('date'),
        },
        items: [
          {
            title: header.lxHeaderModalDateComplimentsTitle,
            description: header.lxHeaderModalDateComplimentsDescription,
            url: `/${lang}/features/compliments/`,
          },
          {
            title: header.lxHeaderModalDateIncognitoTitle,
            description: header.lxHeaderModalDateIncognitoDescription,
            url: `/${lang}/features/incognito-mode/`,
          },
          {
            title: header.lxHeaderModalDateVideoChatTitle,
            description: header.lxHeaderModalDateVideoChatDescription,
            url: `/${lang}/features/video-chat/`,
          },
          {
            title: header.lxHeaderModalDateTravelModeTitle,
            description: header.lxHeaderModalDateTravelModeDescription,
            url: `/${lang}/features/travel-mode/`,
          },
          {
            title: header.lxHeaderModalDateSnoozeModeTitle,
            description: header.lxHeaderModalDateSnoozeModeDescription,
            url: `/${lang}/features/snooze-mode/`,
          },
          {
            title: header.lxHeaderModalDateSuperSwipeTitle,
            description: header.lxHeaderModalDateSuperSwipeDescription,
            url: `/${lang}/features/superswipe/`,
          },
        ],
      },
      onClick: () => onMenuItemClickHandler('date'),
    },
    {
      title: header.lxHeaderItemLabelFriends,
      isActive: activeMenu === 'friends',
      subMenu: {
        title: header.lxHeaderModalFriendsTitle,
        titleUrl: `/${lang}/bff`,
        description: header.lxHeaderModalFriendsDescription,
        backButton: {
          text: 'Back',
          onClick: () => onMenuItemClickHandler('friends'),
        },
        items: [
          {
            title: header.lxHeaderModalFriendsPlansTitle,
            description: header.lxHeaderModalFriendsPlansDescription,
            iconName: Icon.Name.Calendar,
            url: '/bff/plans',
          },
          {
            title: header.lxHeaderModalFriendsCommunityLeadersTitle,
            description: header.lxHeaderModalFriendsCommunityLeadersDescription,
            iconName: Icon.Name.GenericHeart,
            url: '/bumble-honey-ambassadors',
            // onClick: () => onMenuItemClickHandler('ambassadors'),
          },
        ],
      },
    },
    {
      title: header.lxHeaderItemLabelStories,
      isActive: activeMenu === 'stories',
      subMenu: {
        title: header.lxHeaderModalStoriesTitle,
        description: header.lxHeaderModalStoriesDescription,
        backButton: {
          text: 'Back',
          onClick: () => onMenuItemClickHandler('stories'),
        },
        items: [
          {
            title: header.lxHeaderModalStoriesLoveStoriesTitle,
            description: header.lxHeaderModalStoriesLoveStoriesDescription,
            url: '/love-stories',
          },
          {
            title: header.lxHeaderModalStoriesSeasonalTitle,
            description: header.lxHeaderModalStoriesSeasonalDescription,
            url: '/date/seasonal',
          },
          {
            title: header.lxHeaderModalStoriesFriendshipHubTitle,
            description: header.lxHeaderModalStoriesFriendshipHubDescription,
            url: '/bff/friendship',
          },
        ],
      },
    },
    {
      title: header.lxHeaderItemLabelSafety,
      url: 'https://safety.bumble.com',
      onClick: () => onMenuItemClickHandler('safety'),
    },
    {
      title: header.lxHeaderItemLabelSupport,
      url: SUPPORT_URL,
      onClick: () => onMenuItemClickHandler('support'),
    },
  ];

  React.useEffect(() => {
    setActivePages(headerMenuItems, window.location.href);
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, []);

  const handlePathChange = (path: string, language: string) => {
    const locale = getLocaleFromParams({ lang: language });
    if (!locale) {
      return;
    }

    /**
     * Corner case for not found pages
     */
    if (router.pathname === '/[lang]/[...slug]') {
      return (window.location.href = '/');
    }

    /**
     * Route for feature pages
     */
    if (router.pathname.startsWith('/[lang]')) {
      return (window.location.href = router.pathname.replace('/[lang]', path));
    }

    const search = window.location.search === '' ? '' : window.location.search;
    const windowPathName = path + router.pathname + search;

    // @TODO - if we use basePath in next.config.js - use it
    // : window.location.pathname.replace(pathname, path);
    return (window.location.href = windowPathName);
  };

  return (
    <>
      <SkipLink />
      <UIHeaderContainer
        logo={{ variant: 'word-mark', a11yLabel: common.lxA11yLogo }}
        menuItems={headerMenuItems}
        backButtonText="Back"
        languages={languagesList}
        lang={lang}
        downloadUrl="https://bumble.onelink.me/3396940749/yuhjm82c"
        downloadA11yLabel={header.lxA11yDownload}
        // JS-30493: Bumble Web is discontinued and the button is hidden via
        // styles/components/ui-kit-overrides.scss. signInText is required by
        // @bumble/ui-kit's Header - it can only be dropped once ui-kit stops
        // hardcoding the sign-in button (see the follow-up ui-kit ticket).
        signInText={header.lxSignIn}
        getTheAppText={header.lxGetTheApp}
        isDarkMode={isDarkMode}
        hamburgerMenuOpenA11yLabel={common.lxOpenNavigationMenu}
        hamburgerMenuCloseA11yLabel={common.lxCloseNavigationMenu}
        languageA11yLabel={common.lxLanguageSelector}
        onLanguageChange={(language: string) => handlePathChange(`/${language}`, language)}
      />
    </>
  );
};
